import React, { useState } from "react";
import { ChevronDown, ChevronUp, ScanLine, Sparkles } from "lucide-react";

const FIELDS = [
  ["supplier_name", "Lieferant"],
  ["total_amount", "Betrag"],
  ["invoice_date", "Datum"],
  ["invoice_number", "Rg-Nr."],
  ["tax_rate", "MWST"],
  ["iban", "IBAN"],
];

function confCls(v) {
  if (v >= 0.9) return "border-emerald-300 bg-emerald-50 text-emerald-800";
  if (v >= 0.7) return "border-amber-300 bg-amber-50 text-amber-800";
  return "border-rose-300 bg-rose-50 text-rose-800";
}

const fmtNum = (v, d = 2) => (v == null || v === "" ? "" : Number(v).toLocaleString("de-CH", { minimumFractionDigits: d, maximumFractionDigits: d }));

/** Quelle der Belegdaten (Document AI / KI), Konfidenz je Feld und Einzelpositionen. */
export function InboxExtractionDetails({ item }) {
  const ex = item.extracted || {};
  const [open, setOpen] = useState(false);
  const fc = ex.field_confidence || {};
  const items = ex.line_items || [];
  const isDocAI = ex.extraction_engine === "document_ai";
  if (!isDocAI && !ex.extraction_engine) return null;

  return (
    <div className="mt-1.5 text-[10px]" data-testid={`inbox-extraction-${item.id}`}>
      <div className="flex flex-wrap items-center gap-1">
        {isDocAI ? (
          <span className="inline-flex items-center gap-1 rounded border border-teal-300 bg-teal-50 px-1.5 py-px font-medium text-teal-800" title="Felder deterministisch aus dem Dokument gelesen (Google Document AI Invoice Parser)" data-testid={`inbox-engine-${item.id}`}>
            <ScanLine className="h-3 w-3" /> Document AI
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded border border-violet-200 bg-violet-50 px-1.5 py-px font-medium text-violet-700" title="Felder durch KI-Textanalyse ermittelt – bitte prüfen" data-testid={`inbox-engine-${item.id}`}>
            <Sparkles className="h-3 w-3" /> KI-Analyse
          </span>
        )}
        {isDocAI && (() => {
          const checks = item.checks?.checks || [];
          const verified = (k) => (k === "tax_rate" && checks.some((c) => c.id === "arith" && c.status === "green"))
            || (k === "total_amount" && checks.some((c) => c.id === "arith" && c.status === "green"))
            || (k === "iban" && checks.some((c) => c.id === "iban" && c.status === "green"));
          const shown = FIELDS.filter(([k]) => fc[k] != null && ex[k] != null && ex[k] !== "");
          return shown.length > 0 && (
            <span className="inline-flex items-center gap-1 flex-wrap" title="Lesesicherheit je Feld: Wie sicher hat Document AI diesen Wert aus dem Dokument gelesen? (kein Prozentsatz des Belegs)">
              <span className="text-slate-500">gelesen:</span>
              {shown.map(([k, label]) => verified(k) ? (
                <span key={k} className="rounded border border-emerald-300 bg-emerald-50 px-1.5 py-px text-emerald-800" title={`${label}: rechnerisch bestätigt (gelesen mit ${Math.round(fc[k] * 100)} % Sicherheit)`} data-testid={`inbox-conf-${item.id}-${k}`}>
                  {label} ✓ geprüft
                </span>
              ) : (
                <span key={k} className={`rounded border px-1.5 py-px ${confCls(fc[k])}`} title={`${label}: Document AI hat diesen Wert mit ${Math.round(fc[k] * 100)} % Sicherheit gelesen`} data-testid={`inbox-conf-${item.id}-${k}`}>
                  {label} {Math.round(fc[k] * 100)} % sicher
                </span>
              ))}
            </span>
          );
        })()}
        {isDocAI && items.length > 0 && (
          <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-0.5 rounded border border-slate-300 bg-white px-1.5 py-px text-slate-700 hover:bg-slate-50" data-testid={`inbox-line-items-toggle-${item.id}`}>
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} {items.length} Position{items.length === 1 ? "" : "en"}
          </button>
        )}
      </div>
      {open && items.length > 0 && (
        <table className="mt-1 w-full border-collapse rounded border border-slate-200 bg-white text-[10px]" data-testid={`inbox-line-items-${item.id}`}>
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-1.5 py-0.5 text-left font-medium">Beschreibung</th>
              <th className="px-1.5 py-0.5 text-right font-medium">Menge</th>
              <th className="px-1.5 py-0.5 text-right font-medium">Einzelpreis</th>
              <th className="px-1.5 py-0.5 text-right font-medium">Betrag</th>
              <th className="px-1.5 py-0.5 text-right font-medium">Sicher</th>
            </tr>
          </thead>
          <tbody>
            {items.map((li, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-1.5 py-0.5 text-slate-800">{li.description || "–"}{li.product_code ? <span className="ml-1 font-mono text-slate-400">{li.product_code}</span> : null}</td>
                <td className="px-1.5 py-0.5 text-right">{li.quantity != null ? `${fmtNum(li.quantity, Number.isInteger(li.quantity) ? 0 : 2)}${li.unit ? " " + li.unit : ""}` : ""}</td>
                <td className="px-1.5 py-0.5 text-right">{fmtNum(li.unit_price)}</td>
                <td className="px-1.5 py-0.5 text-right font-medium">{fmtNum(li.amount)}</td>
                <td className={`px-1.5 py-0.5 text-right ${li.confidence >= 0.9 ? "text-emerald-700" : li.confidence >= 0.7 ? "text-amber-700" : "text-rose-700"}`}>{li.confidence != null ? `${Math.round(li.confidence * 100)} %` : ""}</td>
              </tr>
            ))}
            {ex.net_amount != null && (
              <tr className="border-t border-slate-200 text-slate-600">
                <td className="px-1.5 py-0.5" colSpan={3}>Netto {fmtNum(ex.net_amount)}{ex.tax_amount != null ? ` · MWST ${fmtNum(ex.tax_amount)}` : ""}</td>
                <td className="px-1.5 py-0.5 text-right font-semibold text-slate-900">{fmtNum(ex.total_amount)}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
